<template>
  <div class="related-guides">
    <h2 class="related-guides__header">{{ t('relatedGuides.title') }}</h2>
    <div class="related-guides__list">
      <related-guide-card
        v-for="guide in mergedGuides"
        :key="guide.id"
        v-bind="guide"
        @guide-clicked="handleGuideClick"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from '@experience/i18n';
import { computed, inject } from 'vue';

import type { ArticleContent } from '@/features/articleContent';
import { mapApiGuideForRelatedCard } from '@/features/articleContent/lib/mapApiGuideForBlocks';
import type { PassionateGuide } from '@/shared/api/experience';

import RelatedGuideCard, { type Props as Guide } from './Card.vue';
import ru from '../i18n/ru.json';

interface GuideInfo {
  id?: number | string;
  guide_id?: number | string;
  description?: ArticleContent | string;
}

interface Props {
  guides?: GuideInfo[];
  guidesData?: Record<string, PassionateGuide>;
}

const props = withDefaults(defineProps<Props>(), { guides: () => [] });

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const sendGuideClickEvent = inject<((guideId: number, clickType: string) => void) | undefined>('sendGuideClickEvent');

const handleGuideClick = (guideId: number) => {
  if (sendGuideClickEvent) {
    sendGuideClickEvent(guideId, 'participant');
  }
};

const normalizeDescription = (description: GuideInfo['description']): ArticleContent | null => {
  if (Array.isArray(description) && description.length > 0) {
    return description;
  }

  if (typeof description === 'string' && description.trim().length > 0) {
    return [
      {
        type: 'paragraph',
        children: [{ type: 'text', raw: description }],
      },
    ] as ArticleContent;
  }

  return null;
};

const normalizedGuidesFromData = computed(() => {
  const guidesDataEntries = Object.entries(props.guidesData ?? {});

  return guidesDataEntries
    .map(([guideKey, guideData]) => {
      const guideIdFromValue = Number(guideData.id);
      const guideIdFromKey = Number(guideKey);
      const guideId = Number.isFinite(guideIdFromValue) ? guideIdFromValue : guideIdFromKey;

      if (!Number.isFinite(guideId)) {
        return null;
      }

      return {
        ...guideData,
        id: guideId,
      };
    })
    .filter((guideData): guideData is PassionateGuide & { id: number } => guideData !== null);
});

const mergedGuides = computed((): Guide[] => {
  const postList = normalizedGuidesFromData.value;
  const byId = new Map(postList.map((api) => [Number(api.id), mapApiGuideForRelatedCard(api)]));
  const result: Guide[] = [];

  for (const rawGuide of props.guides) {
    const guideId = Number(rawGuide.id ?? rawGuide.guide_id);
    const guideDescription = normalizeDescription(rawGuide.description);
    if (!Number.isFinite(guideId) || guideDescription == null) {
      continue;
    }
    const related = byId.get(guideId);
    if (related) {
      result.push({ ...related, description: guideDescription });
    }
  }

  if (result.length === 0) {
    for (const apiGuide of postList) {
      const guideId = Number(apiGuide.id);
      const guideDescription = normalizeDescription(apiGuide.description) ?? [];
      const relatedGuide = byId.get(guideId);
      if (!Number.isFinite(guideId) || !relatedGuide) {
        continue;
      }

      result.push({ ...relatedGuide, description: guideDescription });
    }
  }

  return result;
});
</script>

<style lang="scss" scoped>
.related-guides {
  max-width: var(--width-content);
  margin-top: var(--alias-space-32);
}

.related-guides__header {
  @include base-700-500-extra;
}

.related-guides__list {
  display: flex;
  flex-direction: column;
  gap: var(--alias-space-24);
  margin-top: var(--alias-space-20);

  @include tablet {
    gap: var(--alias-space-16);
  }
}
</style>
